import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import CloseIcon from '@mui/icons-material/Close';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry, AllCommunityModule, themeQuartz } from 'ag-grid-community';
import ReactPlayer from 'react-player';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { API_URL } from '../runtimeConfig';

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

// Export status polling cadence + hard cap (a stuck/lost job errors out instead of
// polling forever). 10 minutes at 3s intervals comfortably covers a full-project
// export plus its server-side retries.
const EXPORT_POLL_INTERVAL_MS = 3000;
const EXPORT_POLL_TIMEOUT_MS = 10 * 60 * 1000;

// Create AG Grid theme (new v33+ approach)
const gridTheme = themeQuartz;

const SurveyDataInteraction = () => {
  const navigate = useNavigate();
  const { userAttributes } = useAuth();
  const gridRef = useRef();
  const videoRef = useRef();
  
  // State management
  const [loading, setLoading] = useState(false);
  // Infinite row model manages row data internally (via the datasource below) —
  // we only track whether a load has happened and how many rows are known so far,
  // not the rows themselves.
  const [hasLoadedData, setHasLoadedData] = useState(false);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [totalRowCountFinal, setTotalRowCountFinal] = useState(false);
  const [columnDefs, setColumnDefs] = useState([]);
  const [filterOptions, setFilterOptions] = useState({
    brand_names: [],
    product_categories: [],
    moment_ids: [],
  });
  const [lastUpdated, setLastUpdated] = useState(null);
  
  // Filter state
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('All');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedMoment, setSelectedMoment] = useState('All');
  const [selectedJourneyStatus, setSelectedJourneyStatus] = useState('All');
  
  // Video modal state
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState('');
  const [currentSubtitles, setCurrentSubtitles] = useState([]);
  const [videoModalLoading, setVideoModalLoading] = useState(false);
  const [videoModalError, setVideoModalError] = useState(null);
  
  // Analytics modal state
  const [analyticsModalOpen, setAnalyticsModalOpen] = useState(false);
  const [currentAnalyticsData, setCurrentAnalyticsData] = useState(null);

  // Export modal state — full-dataset CSV/SPSS export, backed by an async S3 job
  // (see DATA_ACCESS_STRATEGY.md / exportService.js for why it's async).
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportFormats, setExportFormats] = useState({ csv: true, sav: false });
  const [exportJobId, setExportJobId] = useState(null);
  const [exportStatus, setExportStatus] = useState(null); // null | 'processing' | 'complete' | 'error'
  const [exportResult, setExportResult] = useState(null);
  
  // Available projects - dynamically fetched from backend
  const [availableProjects, setAvailableProjects] = useState([]);

  // Project date window from projects_activation_info (authoritative)
  const [projectInfo, setProjectInfo] = useState(null);

  // Column visibility state
  const [columnVisibilityOpen, setColumnVisibilityOpen] = useState(false);
  
  // Fetch available projects on mount (KISS: simple API call)
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();
        if (!token) return;
        
        const response = await axios.get(
          `${API_URL}/api/analytics-views/projects`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data.success) {
          // Server already enforces access control via Cognito groups
          // (admin => all projects; otherwise scoped to the user's group list).
          // Trust it as-is. Re-filtering here double-applied the group check and
          // could wipe the dropdown on any client/server projectAccess skew.
          setAvailableProjects(response.data.projects);
        }
      } catch (error) {
        console.error('Failed to fetch projects:', error);
        // Fallback to Cognito groups if API fails
        if (userAttributes?.projectAccess) {
          setAvailableProjects(userAttributes.projectAccess);
        }
      }
    };
    
    if (userAttributes) {
      fetchProjects();
    }
  }, [userAttributes]);

  // Close column visibility dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (columnVisibilityOpen && !event.target.closest('.column-visibility-container')) {
        setColumnVisibilityOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [columnVisibilityOpen]);

  // Enable subtitle tracks programmatically when video modal opens
  // The `default` attribute alone is not reliable — browsers suppress tracks based on user prefs
  useEffect(() => {
    if (!videoModalOpen || !currentSubtitles.length) return;

    const enableTracks = () => {
      const video = videoRef.current;
      if (!video || !video.textTracks || video.textTracks.length === 0) return;
      // Default to English track; fall back to first track if no English exists
      const hasEnglish = Array.from(video.textTracks).some(t => t.language === 'en');
      for (let i = 0; i < video.textTracks.length; i++) {
        const track = video.textTracks[i];
        track.mode = (hasEnglish ? track.language === 'en' : i === 0) ? 'showing' : 'hidden';
      }
      console.log(`✅ Enabled caption track(s): ${video.textTracks.length} total, defaulting to ${hasEnglish ? 'English' : 'first'}`);
    };

    // Tracks may already be ready, or may load after metadata
    const video = videoRef.current;
    if (video) {
      enableTracks();
      video.addEventListener('loadedmetadata', enableTracks);
      return () => video.removeEventListener('loadedmetadata', enableTracks);
    }
  }, [videoModalOpen, currentSubtitles]);

  // Fetch a presigned video URL + subtitles for a single video, on demand.
  // The bulk dashboard endpoint no longer enriches every row with S3 lookups
  // (that caused 504s on large projects) — this scopes the S3 work to just
  // the one video the user opens, and errors are scoped to that video only.
  const handlePlayVideo = useCallback(async (rawUrl) => {
    setVideoModalOpen(true);
    setVideoModalLoading(true);
    setVideoModalError(null);
    setCurrentVideoUrl('');
    setCurrentSubtitles([]);

    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      if (!token) {
        setVideoModalError('Authentication required. Please sign in again.');
        return;
      }

      const response = await axios.get(
        `${API_URL}/api/analytics-views/media/${selectedProject}?key=${encodeURIComponent(rawUrl)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setCurrentVideoUrl(response.data.videoUrl);
        setCurrentSubtitles(response.data.subtitles || []);
      } else {
        setVideoModalError('Failed to load video.');
      }
    } catch (error) {
      console.error('Failed to fetch media URL:', error);
      setVideoModalError('Failed to load video. Please try again.');
    } finally {
      setVideoModalLoading(false);
    }
  }, [selectedProject]);

  // Generate dynamic columns from first data row (KISS approach)
  const generateColumnsFromData = useCallback((data) => {
    if (!data || data.length === 0) return [];
    
    const firstRow = data[0];
    const columns = [];
    
    // Helper: Get unique values for a field (for Set Filter)
    const getUniqueValues = (field) => {
      const uniqueVals = [...new Set(data.map(row => row[field]).filter(val => val !== null && val !== undefined && val !== ''))];
      return uniqueVals.sort((a, b) => String(a).localeCompare(String(b)));
    };
    
    // Priority columns (always first, checkboxes will appear before these)
    const priorityFields = ['session_id', 'responder_id', 'project_code', 'question_id', 'created_at'];
    
    // Add priority columns first with special config
    priorityFields.forEach(field => {
      if (field in firstRow) {
        const colDef = {
          field,
          headerName: field.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
          width: field === 'session_id' ? 200 : 150,
        };
        
        // Date filtering
        if (field === 'created_at') {
          colDef.filter = 'agDateColumnFilter';
          colDef.filterParams = {
            buttons: ['apply', 'reset'],
            closeOnApply: true,
          };
          colDef.valueFormatter = (params) => {
            if (!params.value) return '';
            return new Date(params.value).toLocaleString();
          };
        } else {
          // ID columns - text filter with apply/reset buttons
          colDef.filter = 'agTextColumnFilter';
          colDef.filterParams = {
            buttons: ['apply', 'reset'],
            closeOnApply: true,
          };
        }
        
        columns.push(colDef);
      }
    });
    
    // Add remaining columns dynamically
    Object.keys(firstRow).forEach(field => {
      if (priorityFields.includes(field)) return; // Skip priority fields
      
      const value = firstRow[field];
      const colDef = {
        field,
        headerName: field.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
        width: 150,
        filter: true, // Default filter
      };
      
      // Smart type detection and configuration with apply/reset buttons
      if (field.includes('confidence') || field.includes('_no')) {
        // Numeric columns - use Number Filter
        colDef.filter = 'agNumberColumnFilter';
        colDef.filterParams = {
          buttons: ['apply', 'reset'],
          closeOnApply: true,
        };
        if (field.includes('confidence')) {
          colDef.valueFormatter = (params) => {
            return params.value ? `${(params.value * 100).toFixed(1)}%` : 'N/A';
          };
        }
        colDef.width = 120;
      } else if (field === 'brand_name' || field === 'product_category' || field === 'moment_id' || 
                 field === 'survey_category' || field === 'language') {
        // Categorical columns - use text filter with buttons
        colDef.filter = 'agTextColumnFilter';
        colDef.filterParams = {
          buttons: ['apply', 'reset'],
          closeOnApply: true,
        };
        colDef.width = 150;
      } else if (field.includes('input') || field.includes('question_text')) {
        // Text columns - use text filter with buttons
        colDef.filter = 'agTextColumnFilter';
        colDef.filterParams = {
          buttons: ['apply', 'reset'],
          closeOnApply: true,
        };
        colDef.width = 250;
        colDef.wrapText = true;
        colDef.autoHeight = true;
      } else if (field === 'analytics_values_complete') {
        // Analytics JSON - Show raw text, click to open clean modal view
        colDef.headerName = 'Analytics Complete';
        colDef.width = 300;
        colDef.wrapText = true;
        colDef.autoHeight = true;
        colDef.cellStyle = { 
          cursor: 'pointer', 
          color: '#0066cc',
          textDecoration: 'underline',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word'
        };
        colDef.valueFormatter = (params) => {
          if (!params.value) return '';
          const jsonStr = typeof params.value === 'string' 
            ? params.value 
            : JSON.stringify(params.value, null, 2);
          // Truncate if too long for display
          return jsonStr.length > 200 
            ? jsonStr.substring(0, 200) + '... (click to view full)' 
            : jsonStr;
        };
        colDef.onCellClicked = (params) => {
          if (params.data.analytics_values_complete) {
            setCurrentAnalyticsData({
              session_id: params.data.session_id,
              question_id: params.data.question_id,
              data: params.data.analytics_values_complete,
              field_count: params.data.analytics_field_count
            });
            setAnalyticsModalOpen(true);
          }
        };
      } else if (field === 'media_video_url') {
        // Video column - Show Play button immediately; presigned URL + subtitles
        // are fetched on-demand when clicked (no bulk S3 enrichment anymore).
        colDef.headerName = 'Video';
        colDef.width = 100;
        colDef.cellStyle = { textAlign: 'center' };
        colDef.cellRenderer = (params) => {
          const rawUrl = params.data.media_video_url;

          if (!rawUrl || rawUrl === 'null' || rawUrl === '') {
            return null;
          }

          // Return a React element so ag-grid-react can render it safely
          return (
            <button
              className="video-play-btn"
              style={{
                background: '#14b8a6',
                color: 'white',
                border: 'none',
                padding: '5px 10px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 500,
              }}
              onClick={() => handlePlayVideo(rawUrl)}
            >
              ▶ Play
            </button>
          );
        };
      } else if (field === 'media_metadata') {
        colDef.hide = true; // Hidden by default
      } else if (typeof value === 'object' && value !== null) {
        // Object columns - show as JSON string
        colDef.valueFormatter = (params) => {
          return params.value ? JSON.stringify(params.value) : '';
        };
      } else {
        // All other columns - default text filter with apply/reset buttons
        colDef.filter = 'agTextColumnFilter';
        colDef.filterParams = {
          buttons: ['apply', 'reset'],
          closeOnApply: true,
        };
      }
      
      columns.push(colDef);
    });
    
    return columns;
  }, [handlePlayVideo]);

  // Columns are generated from the first page of data inside the datasource's
  // getRows() below (infinite row model — there's no single "data loaded" array to
  // watch anymore).

  // Load saved column state from localStorage, once real columns exist
  useEffect(() => {
    if (userAttributes?.username && columnDefs.length > 0) {
      const savedState = localStorage.getItem(`dashboard-layout-${userAttributes.username}`);
      if (savedState && gridRef.current) {
        try {
          const state = JSON.parse(savedState);
          gridRef.current.api.applyColumnState({ state, applyOrder: true });
        } catch (error) {
          console.error('Failed to restore column layout:', error);
        }
      }
    }
  }, [userAttributes, columnDefs]);

  // Save column state on change
  const onColumnMoved = useCallback(() => {
    if (gridRef.current && userAttributes?.username) {
      const columnState = gridRef.current.api.getColumnState();
      localStorage.setItem(
        `dashboard-layout-${userAttributes.username}`,
        JSON.stringify(columnState)
      );
    }
  }, [userAttributes]);

  // Save column state when visibility changes
  const onColumnVisible = useCallback(() => {
    if (gridRef.current && userAttributes?.username) {
      const columnState = gridRef.current.api.getColumnState();
      localStorage.setItem(
        `dashboard-layout-${userAttributes.username}`,
        JSON.stringify(columnState)
      );
    }
  }, [userAttributes]);

  // Toggle column visibility
  const toggleColumnVisibility = (field) => {
    if (gridRef.current) {
      const columnState = gridRef.current.api.getColumnState();
      const column = columnState.find(col => col.colId === field);
      if (column) {
        gridRef.current.api.setColumnsVisible([field], !column.hide);
      }
    }
  };

  // Get all columns for visibility menu
  const getAllColumns = () => {
    if (!columnDefs || columnDefs.length === 0) return [];
    return columnDefs.filter(col => col.field && col.field !== 'ag-Grid-AutoColumn');
  };

  // Check if column is visible
  const isColumnVisible = (field) => {
    if (!gridRef.current) return true;
    const columnState = gridRef.current.api.getColumnState();
    const column = columnState.find(col => col.colId === field);
    return column ? !column.hide : true;
  };

  // Reset column layout
  const resetLayout = () => {
    if (gridRef.current && userAttributes?.username) {
      gridRef.current.api.resetColumnState();
      localStorage.removeItem(`dashboard-layout-${userAttributes.username}`);
    }
  };

  // Clear all filters
  const clearAllFilters = () => {
    if (gridRef.current) {
      gridRef.current.api.setFilterModel(null);
    }
  };

  // Fetch filter options and project info when project changes
  useEffect(() => {
    if (selectedProject) {
      fetchFilterOptions();
      fetchProjectInfo();
    } else {
      setProjectInfo(null);
    }
  }, [selectedProject]);

  const fetchProjectInfo = async () => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      if (!token) return;
      const response = await axios.get(
        `${API_URL}/api/analytics-views/project-info/${selectedProject}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setProjectInfo(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch project info:', error);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      console.log('🔑 Fetching filter options with token:', token ? 'Present' : 'Missing');
      
      if (!token) {
        console.error('❌ No JWT token available. User may not be logged in.');
        alert('Authentication required. Please sign in again.');
        navigate('/login');
        return;
      }
      
      const response = await axios.get(
        `${API_URL}/api/analytics-views/filter-options/${selectedProject}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setFilterOptions(response.data.filter_options);
      }
    } catch (error) {
      console.error('Failed to fetch filter options:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please sign in again.');
        navigate('/login');
      }
    }
  };

  // AG Grid Infinite Row Model datasource — startRow/endRow map directly to the
  // backend's LIMIT/OFFSET (/dashboard/:projectCode/page). Only a sliding window of
  // rows is ever held in the browser, regardless of how large the project is.
  // Captures the current filter dropdown values at the moment it's built; loadData()
  // rebuilds and attaches a fresh one so filter changes only take effect on demand
  // (same "adjust filters, then click Load Data" UX as before).
  const buildDatasource = useCallback(() => ({
    getRows: async (params) => {
      try {
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();

        if (!token) {
          alert('Authentication required. Please sign in again.');
          navigate('/login');
          params.failCallback();
          return;
        }

        const qs = new URLSearchParams({
          startRow: params.startRow,
          endRow: params.endRow,
        });
        if (selectedBrand !== 'All') qs.append('brand_name', selectedBrand);
        if (selectedCategory !== 'All') qs.append('product_category', selectedCategory);
        if (selectedMoment !== 'All') qs.append('moment_id', selectedMoment);
        if (selectedJourneyStatus !== 'All') qs.append('journey_status', selectedJourneyStatus);

        const response = await axios.get(
          `${API_URL}/api/analytics-views/dashboard/${selectedProject}/page?${qs}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const { rows, lastRow } = response.data;
        params.successCallback(rows, lastRow);

        if (params.startRow === 0 && rows.length > 0) {
          setColumnDefs(generateColumnsFromData(rows));
        }
        setTotalRowCount(lastRow === -1 ? params.startRow + rows.length : lastRow);
        setTotalRowCountFinal(lastRow !== -1);
        setLastUpdated(new Date());
        setLoading(false);
      } catch (error) {
        console.error('Failed to fetch dashboard page:', error);
        if (error.response?.status === 401) {
          alert('Session expired. Please sign in again.');
          navigate('/login');
        } else if (params.startRow === 0) {
          alert('Failed to load data. Please try again.');
        }
        setLoading(false);
        params.failCallback();
      }
    },
  }), [selectedProject, selectedBrand, selectedCategory, selectedMoment, selectedJourneyStatus, generateColumnsFromData, navigate]);

  // "Load Data" button — (re)applies whatever filters are currently selected and
  // starts a fresh scroll from the top.
  const loadData = () => {
    if (!selectedProject || !gridRef.current?.api) return;
    setLoading(true);
    setHasLoadedData(true);
    setTotalRowCount(0);
    setTotalRowCountFinal(false);
    gridRef.current.api.setGridOption('datasource', buildDatasource());
  };

  // Refresh button — re-runs the same query (equivalent to re-clicking Load Data).
  const handleRefresh = () => {
    if (hasLoadedData) {
      loadData();
    }
  };

  // Export to CSV
  const exportSelected = () => {
    const selectedRows = gridRef.current.api.getSelectedRows();
    if (selectedRows.length === 0) {
      alert('Please select rows to export');
      return;
    }
    gridRef.current.api.exportDataAsCsv({
      onlySelected: true,
      fileName: `survey-data-selected-${new Date().toISOString().split('T')[0]}.csv`,
    });
  };

  // "Export All" opens the format picker rather than exporting client-side —
  // the grid only ever holds a sliding window of rows (infinite row model), so a
  // client-side export would silently be incomplete. The real export runs
  // server-side against the full filtered dataset.
  const exportAll = () => {
    setExportFormats({ csv: true, sav: false });
    setExportJobId(null);
    setExportStatus(null);
    setExportResult(null);
    setExportModalOpen(true);
  };

  const startExport = async () => {
    const formats = Object.entries(exportFormats).filter(([, v]) => v).map(([k]) => k);
    if (formats.length === 0) return;

    setExportStatus('processing');
    setExportResult(null);

    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      if (!token) {
        alert('Authentication required. Please sign in again.');
        navigate('/login');
        return;
      }

      const body = { formats };
      if (selectedBrand !== 'All') body.brand_name = selectedBrand;
      if (selectedCategory !== 'All') body.product_category = selectedCategory;
      if (selectedMoment !== 'All') body.moment_id = selectedMoment;
      if (selectedJourneyStatus !== 'All') body.journey_status = selectedJourneyStatus;

      const { data } = await axios.post(
        `${API_URL}/api/analytics-views/dashboard/${selectedProject}/export`,
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setExportJobId(data.jobId);
    } catch (error) {
      console.error('Failed to start export:', error);
      setExportStatus('error');
      setExportResult({ error: 'Failed to start export. Please try again.' });
    }
  };

  // Poll export job status every 3s until it lands on complete/error. Status lives
  // server-side in Redis (not per-machine memory), so this works regardless of
  // which of the app's machines actually ran the job. Hard-capped at EXPORT_POLL_TIMEOUT
  // so a stuck/lost job surfaces an error to the user instead of spinning forever.
  useEffect(() => {
    if (!exportJobId || exportStatus !== 'processing') return;
    let cancelled = false;
    let interval;
    let attempts = 0;
    const maxAttempts = Math.ceil(EXPORT_POLL_TIMEOUT_MS / EXPORT_POLL_INTERVAL_MS);

    const stop = () => { cancelled = true; if (interval) clearInterval(interval); };

    const poll = async () => {
      attempts += 1;
      try {
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();
        const { data } = await axios.get(
          `${API_URL}/api/analytics-views/dashboard/${selectedProject}/export/${exportJobId}/status`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (cancelled) return;
        if (data.status === 'complete' || data.status === 'error') {
          setExportStatus(data.status);
          setExportResult(data);
          stop();
          return;
        }
      } catch (error) {
        if (!cancelled) console.error('Failed to poll export status:', error);
      }
      if (!cancelled && attempts >= maxAttempts) {
        setExportStatus('error');
        setExportResult({
          error: 'The export is taking longer than expected. It may still complete — please try again in a few minutes.',
        });
        stop();
      }
    };

    poll();
    interval = setInterval(poll, EXPORT_POLL_INTERVAL_MS);
    return stop;
  }, [exportJobId, exportStatus, selectedProject]);

  // Master-detail for analytics_values_complete
  const detailCellRenderer = useCallback((params) => {
    if (!params.data.analytics_values_complete) {
      return '<div style="padding: 20px;">No analytics data available</div>';
    }

    let analyticsData;
    try {
      analyticsData = typeof params.data.analytics_values_complete === 'string'
        ? JSON.parse(params.data.analytics_values_complete)
        : params.data.analytics_values_complete;
    } catch (error) {
      return '<div style="padding: 20px; color: red;">Invalid JSON data</div>';
    }

    const rows = Object.entries(analyticsData)
      .map(([key, value]) => {
        const displayValue = typeof value === 'object' 
          ? JSON.stringify(value) 
          : String(value);
        return `
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #ddd; font-weight: 600;">${key}</td>
            <td style="padding: 8px; border-bottom: 1px solid #ddd;">${displayValue}</td>
          </tr>
        `;
      })
      .join('');

    return `
      <div style="padding: 20px; background: #f5f5f5;">
        <h4 style="margin-top: 0; color: #1976d2;">Analytics Details (${params.data.analytics_field_count} fields)</h4>
        <table style="width: 100%; background: white; border-collapse: collapse; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
          <thead>
            <tr style="background: #1976d2; color: white;">
              <th style="padding: 10px; text-align: left; width: 30%;">Analytics Key</th>
              <th style="padding: 10px; text-align: left;">Value</th>
            </tr>
          </thead>
          <tbody>
            ${rows}
          </tbody>
        </table>
      </div>
    `;
  }, []);

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/')}>
          <ArrowBackIcon /> Back to Home
        </BackButton>
        <Title>Survey Data Interaction</Title>
      </Header>
      
      <Content>
        <ControlPanel>
          <FilterSection>
            <FilterLabel>Project Code:</FilterLabel>
            <FilterSelect 
              value={selectedProject} 
              onChange={(e) => setSelectedProject(e.target.value)}
            >
              <option value="">Select Project</option>
              {availableProjects.map(project => (
                <option key={project} value={project}>{project}</option>
              ))}
            </FilterSelect>

            <FilterLabel>Brand:</FilterLabel>
            <FilterSelect 
              value={selectedBrand} 
              onChange={(e) => setSelectedBrand(e.target.value)}
              disabled={!selectedProject}
            >
              <option value="All">All</option>
              {filterOptions.brand_names.map(brand => (
                <option key={brand} value={brand}>{brand}</option>
              ))}
            </FilterSelect>

            <FilterLabel>Category:</FilterLabel>
            <FilterSelect 
              value={selectedCategory} 
              onChange={(e) => setSelectedCategory(e.target.value)}
              disabled={!selectedProject}
            >
              <option value="All">All</option>
              {filterOptions.product_categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </FilterSelect>

            <FilterLabel>Moment:</FilterLabel>
            <FilterSelect
              value={selectedMoment}
              onChange={(e) => setSelectedMoment(e.target.value)}
              disabled={!selectedProject}
            >
              <option value="All">All</option>
              {filterOptions.moment_ids.map(moment => (
                <option key={moment} value={moment}>{moment}</option>
              ))}
            </FilterSelect>

            <FilterLabel>Journey:</FilterLabel>
            <FilterSelect
              value={selectedJourneyStatus}
              onChange={(e) => setSelectedJourneyStatus(e.target.value)}
              disabled={!selectedProject}
            >
              <option value="All">All</option>
              <option value="complete">Complete</option>
              <option value="partial">Partial</option>
            </FilterSelect>

            <ActionButton onClick={loadData} disabled={!selectedProject || loading}>
              {loading ? 'Loading...' : 'Load Data'}
            </ActionButton>

            {projectInfo && (
              <ProjectDateBadge>
                <StatusDot active={projectInfo.status === 'active'} title={projectInfo.status} />
                <strong>{projectInfo.project_name}</strong>
                <span>&nbsp;|&nbsp;Study Period:&nbsp;</span>
                <strong>{String(projectInfo.start_date).substring(0, 10)} 00:00:00</strong>
                <span>&nbsp;→&nbsp;</span>
                <strong>{projectInfo.end_date ? `${String(projectInfo.end_date).substring(0, 10)} 23:00:00` : 'Open'}</strong>
              </ProjectDateBadge>
            )}
          </FilterSection>

          <ExportSection>
            <ActionButton onClick={handleRefresh} disabled={!hasLoadedData || loading} title="Refresh to get latest data">
              <RefreshIcon /> {loading ? 'Refreshing...' : 'Refresh'}
            </ActionButton>
            <ActionButton onClick={clearAllFilters} disabled={!hasLoadedData} title="Clear all column filters">
              Clear Filters
            </ActionButton>
            <ActionButton onClick={exportSelected} disabled={!hasLoadedData}>
              <DownloadIcon /> Export Selected
            </ActionButton>
            <ActionButton onClick={exportAll} disabled={!hasLoadedData}>
              <DownloadIcon /> Export All
            </ActionButton>
            <ActionButton onClick={resetLayout} $variant="secondary" title="Reset column order and visibility">
              Reset Layout
            </ActionButton>
          </ExportSection>
        </ControlPanel>

        {lastUpdated && (
          <StatusBar>
            <StatusText>
              Last updated: {lastUpdated.toLocaleString()} |
              Total rows: {totalRowCount}{totalRowCountFinal ? '' : '+'}
            </StatusText>
          </StatusBar>
        )}

        <GridContainer>
          <AgGridReact
            ref={gridRef}
            rowModelType="infinite"
            cacheBlockSize={200}
            maxBlocksInCache={10}
            columnDefs={columnDefs}
            theme={gridTheme}
            defaultColDef={{
              // Column sort is off under the infinite row model: the datasource
              // always returns created_at DESC server-side and doesn't honor
              // AG Grid's sortModel, so a clickable sort would look applied without
              // actually reordering fetched pages. Revisit if the team wants
              // server-side sort on additional indexed columns.
              sortable: false,
              filter: true,
              resizable: true,
              floatingFilter: true, // Simple - same as created_at
            }}
            rowSelection={{
              mode: 'multiRow',
              checkboxes: true,
              headerCheckbox: true,
            }}
            onColumnMoved={onColumnMoved}
            onColumnVisible={onColumnVisible}
            animateRows={true}
          />
        </GridContainer>

        {!hasLoadedData && !loading && (
          <EmptyState>
            <EmptyIcon>📊</EmptyIcon>
            <EmptyText>Select a project and click "Load Data" to view survey responses</EmptyText>
          </EmptyState>
        )}
      </Content>

      {/* Video Modal */}
      {videoModalOpen && (
        <VideoModal>
          <ModalOverlay onClick={() => setVideoModalOpen(false)} />
          <ModalContent>
            <CloseButton onClick={() => setVideoModalOpen(false)}>
              <CloseIcon />
            </CloseButton>
            {videoModalLoading ? (
              <VideoModalStatus>Loading video…</VideoModalStatus>
            ) : videoModalError ? (
              <VideoModalStatus>{videoModalError}</VideoModalStatus>
            ) : (
            <video
              ref={videoRef}
              src={currentVideoUrl}
              controls
              autoPlay
              crossOrigin="anonymous"
              style={{ width: '100%', height: '100%' }}
            >
              {currentSubtitles && currentSubtitles.length > 0 && currentSubtitles
                .filter(subtitle => {
                  // Only use VTT subtitles - HTML5 video doesn't support SRT natively
                  const isVtt = typeof subtitle === 'object' 
                    ? (subtitle.format || '').toLowerCase() === 'vtt'
                    : (subtitle || '').toLowerCase().includes('.vtt');
                  
                  if (!isVtt) {
                    console.warn('⚠️ Skipping non-VTT subtitle:', subtitle);
                  }
                  return isVtt;
                })
                .map((subtitle, index) => {
                  // Support both object format {url, label, format} and string format (direct VTT URL)
                  const subtitleUrl = typeof subtitle === 'string' ? subtitle : subtitle.url;
                  const subtitleLabel = typeof subtitle === 'object' ? (subtitle.label || 'English') : 'English';
                  const subtitleLang = typeof subtitle === 'object' ? (subtitle.lang || 'en') : 'en';
                  
                  console.log(`✅ Loading subtitle track ${index + 1}:`, subtitleUrl);
                  
                  return (
                    <track
                      key={index}
                      kind="subtitles"
                      src={subtitleUrl}
                      srcLang={subtitleLang}
                      label={subtitleLabel}
                      default={subtitleLang === 'en'}
                    />
                  );
                })}
              Your browser does not support the video element.
            </video>
            )}
          </ModalContent>
        </VideoModal>
      )}

      {/* Analytics Modal */}
      {analyticsModalOpen && currentAnalyticsData && (
        <AnalyticsModal>
          <ModalOverlay onClick={() => setAnalyticsModalOpen(false)} />
          <AnalyticsModalContent>
            <AnalyticsHeader>
              <AnalyticsTitle>
                📊 Analytics Data
                <AnalyticsSubtitle>
                  Session: {currentAnalyticsData.session_id} | Question: {currentAnalyticsData.question_id}
                </AnalyticsSubtitle>
              </AnalyticsTitle>
              <CloseButton onClick={() => setAnalyticsModalOpen(false)}>
                <CloseIcon />
              </CloseButton>
            </AnalyticsHeader>
            <AnalyticsTable>
              <thead>
                <tr>
                  <AnalyticsTableHeader>Analytics Key</AnalyticsTableHeader>
                  <AnalyticsTableHeader>Value</AnalyticsTableHeader>
                </tr>
              </thead>
              <tbody>
                {Object.entries(
                  typeof currentAnalyticsData.data === 'string' 
                    ? JSON.parse(currentAnalyticsData.data) 
                    : currentAnalyticsData.data
                ).map(([key, value]) => (
                  <tr key={key}>
                    <AnalyticsTableCell $isKey>{key}</AnalyticsTableCell>
                    <AnalyticsTableCell>
                      {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                    </AnalyticsTableCell>
                  </tr>
                ))}
              </tbody>
            </AnalyticsTable>
            <AnalyticsFooter>
              Total Fields: {currentAnalyticsData.field_count}
            </AnalyticsFooter>
          </AnalyticsModalContent>
        </AnalyticsModal>
      )}

      {/* Export Modal */}
      {exportModalOpen && (
        <AnalyticsModal>
          <ModalOverlay onClick={() => { if (exportStatus !== 'processing') setExportModalOpen(false); }} />
          <ExportModalContent>
            <AnalyticsHeader>
              <AnalyticsTitle>
                Export Data
                <AnalyticsSubtitle>Project: {selectedProject}</AnalyticsSubtitle>
              </AnalyticsTitle>
              {exportStatus !== 'processing' && (
                <CloseButton onClick={() => setExportModalOpen(false)}>
                  <CloseIcon />
                </CloseButton>
              )}
            </AnalyticsHeader>

            <ExportModalBody>
              {!exportJobId && exportStatus !== 'error' && (
                <>
                  <ExportFormatLabel>
                    <input
                      type="checkbox"
                      checked={exportFormats.csv}
                      onChange={(e) => setExportFormats((f) => ({ ...f, csv: e.target.checked }))}
                    />
                    CSV
                  </ExportFormatLabel>
                  <ExportFormatLabel>
                    <input
                      type="checkbox"
                      checked={exportFormats.sav}
                      onChange={(e) => setExportFormats((f) => ({ ...f, sav: e.target.checked }))}
                    />
                    SPSS (.sav)
                  </ExportFormatLabel>
                  <ActionButton
                    onClick={startExport}
                    disabled={!exportFormats.csv && !exportFormats.sav}
                  >
                    Start Export
                  </ActionButton>
                </>
              )}

              {exportStatus === 'processing' && (
                <ExportModalStatus>
                  Preparing your export — this can take a minute or more for large projects.
                </ExportModalStatus>
              )}

              {exportStatus === 'complete' && exportResult && (
                <>
                  <ExportModalStatus>
                    {exportResult.rowCount === 0
                      ? 'No rows matched the current filters.'
                      : `${exportResult.rowCount} rows exported.`}
                  </ExportModalStatus>
                  {exportResult.csvUrl && (
                    <ExportDownloadLink href={exportResult.csvUrl}>⬇ Download CSV</ExportDownloadLink>
                  )}
                  {exportResult.savUrl && (
                    <ExportDownloadLink href={exportResult.savUrl}>⬇ Download SPSS (.sav)</ExportDownloadLink>
                  )}
                  {exportResult.savError && (
                    <ExportModalStatus>
                      SPSS conversion failed — the CSV above is still complete and available.
                    </ExportModalStatus>
                  )}
                </>
              )}

              {exportStatus === 'error' && (
                <ExportModalStatus>{exportResult?.error || 'Export failed. Please try again.'}</ExportModalStatus>
              )}
            </ExportModalBody>
          </ExportModalContent>
        </AnalyticsModal>
      )}
    </Container>
  );
};

export default SurveyDataInteraction;

const Container = styled.div`
  min-height: 100vh;
  background-color: ${(props) => props.theme.colors.background};
`;

const Header = styled.header`
  background: linear-gradient(135deg, ${(props) => props.theme.colors.primary} 0%, ${(props) => props.theme.colors.primaryDark} 100%);
  padding: ${(props) => props.theme.spacing.xl};
  box-shadow: ${(props) => props.theme.shadows.md};
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing.sm};
  background: rgba(255, 255, 255, 0.1);
  color: ${(props) => props.theme.colors.textPrimary};
  padding: ${(props) => props.theme.spacing.sm} ${(props) => props.theme.spacing.md};
  border-radius: ${(props) => props.theme.borderRadius.md};
  border: 1px solid rgba(255, 255, 255, 0.2);
  font-size: ${(props) => props.theme.fontSizes.sm};
  margin-bottom: ${(props) => props.theme.spacing.md};
  transition: all ${(props) => props.theme.transitions.fast};
  cursor: pointer;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const Title = styled.h1`
  font-size: ${(props) => props.theme.fontSizes['3xl']};
  font-weight: ${(props) => props.theme.fontWeights.bold};
  color: ${(props) => props.theme.colors.textPrimary};
`;

const Content = styled.main`
  max-width: 95%;
  margin: 0 auto;
  padding: ${(props) => props.theme.spacing.xl};
`;

const ControlPanel = styled.div`
  background-color: ${(props) => props.theme.colors.cardBackground};
  border-radius: ${(props) => props.theme.borderRadius.lg};
  padding: ${(props) => props.theme.spacing.lg};
  margin-bottom: ${(props) => props.theme.spacing.lg};
  box-shadow: ${(props) => props.theme.shadows.sm};
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${(props) => props.theme.spacing.md};
`;

const FilterSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing.md};
  flex-wrap: wrap;
`;

const ProjectDateBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: ${(props) => props.theme.colors.textSecondary};
  background: ${(props) => props.theme.colors.surface};
  border: 1px solid ${(props) => props.theme.colors.border};
  border-radius: 4px;
  padding: 4px 10px;
  white-space: nowrap;
`;

const StatusDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(props) => props.active ? '#22c55e' : '#f59e0b'};
  margin-left: 4px;
`;

const FilterLabel = styled.label`
  font-size: ${(props) => props.theme.fontSizes.sm};
  font-weight: ${(props) => props.theme.fontWeights.semibold};
  color: ${(props) => props.theme.colors.textSecondary};
`;

const FilterSelect = styled.select`
  padding: ${(props) => props.theme.spacing.sm} ${(props) => props.theme.spacing.md};
  border-radius: ${(props) => props.theme.borderRadius.md};
  border: 1px solid ${(props) => props.theme.colors.border};
  background-color: white;
  font-size: ${(props) => props.theme.fontSizes.sm};
  cursor: pointer;
  min-width: 150px;
  
  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
`;

const ExportSection = styled.div`
  display: flex;
  gap: ${(props) => props.theme.spacing.sm};
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing.sm};
  padding: ${(props) => props.theme.spacing.sm} ${(props) => props.theme.spacing.md};
  border-radius: ${(props) => props.theme.borderRadius.md};
  border: none;
  font-size: ${(props) => props.theme.fontSizes.sm};
  font-weight: ${(props) => props.theme.fontWeights.semibold};
  cursor: pointer;
  transition: all ${(props) => props.theme.transitions.fast};
  
  background-color: ${(props) => props.$variant === 'secondary' ? '#6c757d' : props.theme.colors.primary};
  color: white;
  
  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ColumnVisibilityContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const ColumnVisibilityDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  background: white;
  border-radius: ${(props) => props.theme.borderRadius.md};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  min-width: 250px;
  max-height: 400px;
  overflow-y: auto;
  padding: ${(props) => props.theme.spacing.sm};
`;

const ColumnVisibilityHeader = styled.div`
  padding: ${(props) => props.theme.spacing.sm};
  font-weight: ${(props) => props.theme.fontWeights.semibold};
  color: ${(props) => props.theme.colors.textPrimary};
  border-bottom: 1px solid ${(props) => props.theme.colors.border};
  margin-bottom: ${(props) => props.theme.spacing.sm};
`;

const ColumnVisibilityItem = styled.label`
  display: flex;
  align-items: center;
  padding: ${(props) => props.theme.spacing.sm};
  cursor: pointer;
  border-radius: ${(props) => props.theme.borderRadius.sm};
  transition: background-color ${(props) => props.theme.transitions.fast};
  
  &:hover {
    background-color: ${(props) => props.theme.colors.backgroundAlt};
  }
  
  input {
    margin-right: ${(props) => props.theme.spacing.sm};
    cursor: pointer;
  }
  
  span {
    font-size: ${(props) => props.theme.fontSizes.sm};
    color: ${(props) => props.theme.colors.textPrimary};
  }
`;

const StatusBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${(props) => props.theme.spacing.sm} ${(props) => props.theme.spacing.md};
  background-color: ${(props) => props.theme.colors.cardBackground};
  border-radius: ${(props) => props.theme.borderRadius.md};
  margin-bottom: ${(props) => props.theme.spacing.md};
  border: 1px solid ${(props) => props.theme.colors.border};
`;

const StatusText = styled.span`
  font-size: ${(props) => props.theme.fontSizes.sm};
  color: ${(props) => props.theme.colors.textSecondary};
  font-weight: ${(props) => props.theme.fontWeights.medium};
`;

const GridContainer = styled.div`
  height: 600px;
  width: 100%;
  background-color: white;
  border-radius: ${(props) => props.theme.borderRadius.lg};
  box-shadow: ${(props) => props.theme.shadows.md};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${(props) => props.theme.spacing['3xl']};
  background-color: ${(props) => props.theme.colors.cardBackground};
  border-radius: ${(props) => props.theme.borderRadius.lg};
  margin-top: ${(props) => props.theme.spacing.lg};
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  margin-bottom: ${(props) => props.theme.spacing.md};
`;

const EmptyText = styled.p`
  font-size: ${(props) => props.theme.fontSizes.lg};
  color: ${(props) => props.theme.colors.textSecondary};
`;

const VideoModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
`;

const ModalContent = styled.div`
  position: relative;
  width: 80%;
  max-width: 1200px;
  height: 70%;
  background-color: black;
  border-radius: ${(props) => props.theme.borderRadius.lg};
  overflow: hidden;
  z-index: 10000;
`;

const VideoModalStatus = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: ${(props) => props.theme.fontSizes.md};
  text-align: center;
  padding: ${(props) => props.theme.spacing.lg};
`;

const AnalyticsModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AnalyticsModalContent = styled.div`
  position: relative;
  width: 90%;
  max-width: 1000px;
  max-height: 80vh;
  background-color: white;
  border-radius: ${(props) => props.theme.borderRadius.lg};
  overflow: hidden;
  z-index: 10000;
  display: flex;
  flex-direction: column;
`;

const AnalyticsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: ${(props) => props.theme.spacing.lg};
  background: linear-gradient(135deg, #14b8a6 0%, #0d9488 100%);
  color: white;
`;

const AnalyticsTitle = styled.div`
  font-size: ${(props) => props.theme.fontSizes.xl};
  font-weight: ${(props) => props.theme.fontWeights.bold};
`;

const AnalyticsSubtitle = styled.div`
  font-size: ${(props) => props.theme.fontSizes.sm};
  opacity: 0.9;
  margin-top: ${(props) => props.theme.spacing.xs};
`;

const AnalyticsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  overflow-y: auto;
  max-height: calc(80vh - 150px);
  display: block;
`;

const AnalyticsTableHeader = styled.th`
  background-color: #14b8a6;
  color: white;
  padding: ${(props) => props.theme.spacing.md};
  text-align: left;
  font-weight: ${(props) => props.theme.fontWeights.semibold};
  position: sticky;
  top: 0;
  z-index: 1;
  border-bottom: 2px solid #0d9488;
`;

const AnalyticsTableCell = styled.td`
  padding: ${(props) => props.theme.spacing.md};
  border-bottom: 1px solid #e5e7eb;
  vertical-align: top;
  color: #374151;
  ${(props) => props.$isKey && `
    font-weight: ${props.theme.fontWeights.semibold};
    background-color: #f3f4f6;
    color: #111827;
    width: 30%;
  `}
  white-space: pre-wrap;
  word-break: break-word;
`;

const AnalyticsFooter = styled.div`
  padding: ${(props) => props.theme.spacing.md} ${(props) => props.theme.spacing.lg};
  background-color: #f5f5f5;
  font-size: ${(props) => props.theme.fontSizes.sm};
  color: ${(props) => props.theme.colors.textSecondary};
  border-top: 1px solid #ddd;
`;

const ExportModalContent = styled.div`
  position: relative;
  width: 90%;
  max-width: 420px;
  background-color: ${(props) => props.theme.colors.backgroundLight};
  border: 1px solid ${(props) => props.theme.colors.border};
  border-radius: ${(props) => props.theme.borderRadius.lg};
  overflow: hidden;
  z-index: 10000;
  display: flex;
  flex-direction: column;
`;

const ExportModalBody = styled.div`
  padding: ${(props) => props.theme.spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing.md};
`;

const ExportFormatLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing.sm};
  font-size: ${(props) => props.theme.fontSizes.md};
  color: ${(props) => props.theme.colors.textPrimary};
  cursor: pointer;

  input {
    accent-color: #14b8a6;
    width: 16px;
    height: 16px;
    cursor: pointer;
  }
`;

const ExportModalStatus = styled.div`
  font-size: ${(props) => props.theme.fontSizes.md};
  color: ${(props) => props.theme.colors.textSecondary};
`;

const ExportDownloadLink = styled.a`
  display: inline-block;
  padding: ${(props) => props.theme.spacing.sm} ${(props) => props.theme.spacing.md};
  background-color: #14b8a6;
  color: white;
  border-radius: ${(props) => props.theme.borderRadius.md};
  text-decoration: none;
  font-weight: ${(props) => props.theme.fontWeights.bold};
  text-align: center;

  &:hover {
    background-color: #0d9488;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 10001;
  background-color: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  transition: background-color ${(props) => props.theme.transitions.fast};
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.3);
  }
`;
